import * as i0 from '@angular/core';
import { Injectable, InjectionToken, input, inject, booleanAttribute, computed, effect, HostListener, Input, Directive, NgModule } from '@angular/core';
import { NgControl } from '@angular/forms';
import { PARENT_INSTANCE } from 'primeng/basecomponent';
import { BaseModelHolder } from 'primeng/basemodelholder';
import * as i1 from 'primeng/bind';
import { Bind } from 'primeng/bind';
import { Fluid } from 'primeng/fluid';
import { style as style$1 } from '@primeuix/styles/inputtext';
import { BaseStyle } from 'primeng/base';
const style = /*css*/`
    ${style$1}

    /* For PrimeNG */
   .p-inputtext.ng-invalid.ng-dirty {
        border-color: dt('inputtext.invalid.border.color');
    }

    .p-inputtext.ng-invalid.ng-dirty::placeholder {
        color: dt('inputtext.invalid.placeholder.color');
    }
`;
const classes = {
  root: ({
    instance
  }) => ['p-inputtext p-component', {
    'p-filled': instance.$filled(),
    'p-inputtext-sm': instance.pSize === 'small',
    'p-inputtext-lg': instance.pSize === 'large',
    'p-invalid': instance.invalid(),
    'p-variant-filled': instance.$variant() === 'filled',
    'p-inputtext-fluid': instance.hasFluid
  }]
};
let InputTextStyle = /*#__PURE__*/(() => {
  class InputTextStyle extends BaseStyle {
    name = 'inputtext';
    style = style;
    classes = classes;
    static ɵfac = /* @__PURE__ */(() => {
      let ɵInputTextStyle_BaseFactory;
      return function InputTextStyle_Factory(__ngFactoryType__) {
        return (ɵInputTextStyle_BaseFactory || (ɵInputTextStyle_BaseFactory = i0.ɵɵgetInheritedFactory(InputTextStyle)))(__ngFactoryType__ || InputTextStyle);
      };
    })();
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: InputTextStyle,
      factory: InputTextStyle.ɵfac
    });
  }
  return InputTextStyle;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 *
 * InputText renders a text field to enter data.
 *
 * [Live Demo](https://www.primeng.org/inputtext/)
 *
 * @module inputtextstyle
 *
 */
var InputTextClasses = /*#__PURE__*/function (InputTextClasses) {
  /**
   * The class of root element
   */
  InputTextClasses["root"] = "p-inputtext";
  return InputTextClasses;
}(InputTextClasses || {});
const INPUTTEXT_INSTANCE = /*#__PURE__*/new InjectionToken('INPUTTEXT_INSTANCE');
/**
 * InputText directive is an extension to standard input element with theming.
 * @group Components
 */
let InputText = /*#__PURE__*/(() => {
  class InputText extends BaseModelHolder {
    componentName = 'InputText';
    hostName = '';
    /**
     * Used to pass attributes to DOM elements inside the InputText component.
     * @defaultValue undefined
     * @deprecated use pInputTextPT instead.
     * @group Props
     */
    ptInputText = input(...(ngDevMode ? [undefined, {
      debugName: "ptInputText"
    }] : []));
    /**
     * Used to pass attributes to DOM elements inside the InputText component.
     * @defaultValue undefined
     * @group Props
     */
    pInputTextPT = input(...(ngDevMode ? [undefined, {
      debugName: "pInputTextPT"
    }] : []));
    /**
     * Indicates whether the component should be rendered without styles.
     * @defaultValue undefined
     * @group Props
     */
    pInputTextUnstyled = input(...(ngDevMode ? [undefined, {
      debugName: "pInputTextUnstyled"
    }] : []));
    bindDirectiveInstance = inject(Bind, {
      self: true
    });
    $pcInputText = inject(INPUTTEXT_INSTANCE, {
      optional: true,
      skipSelf: true
    }) ?? undefined;
    ngControl = inject(NgControl, {
      optional: true,
      self: true
    });
    pcFluid = inject(Fluid, {
      optional: true,
      host: true,
      skipSelf: true
    });
    /**
     * Defines the size of the component.
     * @group Props
     */
    pSize;
    /**
     * Specifies the input variant of the component.
     * @defaultValue undefined
     * @group Props
     */
    variant = input(...(ngDevMode ? [undefined, {
      debugName: "variant"
    }] : []));
    /**
     * Spans 100% width of the container when enabled.
     * @defaultValue undefined
     * @group Props
     */
    fluid = input(undefined, {
      ...(ngDevMode ? {
        debugName: "fluid"
      } : {}),
      transform: booleanAttribute
    });
    /**
     * When present, it specifies that the component should have invalid state style.
     * @defaultValue false
     * @group Props
     */
    invalid = input(undefined, {
      ...(ngDevMode ? {
        debugName: "invalid"
      } : {}),
      transform: booleanAttribute
    });
    $variant = computed(() => this.variant() || this.config.inputStyle() || this.config.inputVariant(), ...(ngDevMode ? [{
      debugName: "$variant"
    }] : []));
    _componentStyle = inject(InputTextStyle);
    constructor() {
      super();
      effect(() => {
        const pt = this.ptInputText() || this.pInputTextPT();
        pt && this.directivePT.set(pt);
      });
      effect(() => {
        this.pInputTextUnstyled() && this.directiveUnstyled.set(this.pInputTextUnstyled());
      });
    }
    onAfterViewInit() {
      this.writeModelValue(this.ngControl?.value ?? this.el.nativeElement.value);
      this.cd.detectChanges();
    }
    onAfterViewChecked() {
      this.bindDirectiveInstance.setAttrs(this.ptm('root'));
    }
    onDoCheck() {
      this.writeModelValue(this.ngControl?.value ?? this.el.nativeElement.value);
    }
    onInput() {
      this.writeModelValue(this.ngControl?.value ?? this.el.nativeElement.value);
    }
    get hasFluid() {
      return this.fluid() ?? !!this.pcFluid;
    }
    get dataP() {
      return this.cn({
        invalid: this.invalid(),
        fluid: this.hasFluid,
        filled: this.$variant() === 'filled',
        [this.pSize]: this.pSize
      });
    }
    static ɵfac = function InputText_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || InputText)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: InputText,
      selectors: [["", "pInputText", ""]],
      hostVars: 3,
      hostBindings: function InputText_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("input", function InputText_input_HostBindingHandler() {
            return ctx.onInput();
          });
        }
        if (rf & 2) {
          i0.ɵɵattribute("data-p", ctx.dataP);
          i0.ɵɵclassMap(ctx.cx("root"));
        }
      },
      inputs: {
        hostName: "hostName",
        ptInputText: [1, "ptInputText"],
        pInputTextPT: [1, "pInputTextPT"],
        pInputTextUnstyled: [1, "pInputTextUnstyled"],
        pSize: "pSize",
        variant: [1, "variant"],
        fluid: [1, "fluid"],
        invalid: [1, "invalid"]
      },
      features: [i0.ɵɵProvidersFeature([InputTextStyle, {
        provide: INPUTTEXT_INSTANCE,
        useExisting: InputText
      }, {
        provide: PARENT_INSTANCE,
        useExisting: InputText
      }]), i0.ɵɵHostDirectivesFeature([i1.Bind]), i0.ɵɵInheritDefinitionFeature]
    });
  }
  return InputText;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let InputTextModule = /*#__PURE__*/(() => {
  class InputTextModule {
    static ɵfac = function InputTextModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || InputTextModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: InputTextModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return InputTextModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { InputText, InputTextClasses, InputTextModule, InputTextStyle };
